/**
 * Serviço de cache para avatares do S3
 * Armazena URLs do S3 no localStorage para evitar múltiplas requisições
 */

interface CachedAvatar {
  url: string;
  timestamp: number;
  expiresAt: number;
}

class AvatarCacheService {
  private readonly CACHE_KEY_PREFIX = 'avatar_cache_';
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas em ms

  /**
   * Obtém avatar do cache se ainda válido
   * @param userId ID do usuário
   * @returns URL do avatar ou null se não encontrado/expirado
   */
  getCachedAvatar(userId: string): string | null {
    try {
      const cacheKey = `${this.CACHE_KEY_PREFIX}${userId}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;

      const avatarData: CachedAvatar = JSON.parse(cached);
      const now = Date.now();

      // Verificar se o cache ainda é válido
      if (now > avatarData.expiresAt) {
        this.removeCachedAvatar(userId);
        return null;
      }

      return avatarData.url;
    } catch (error) {
      console.error('Erro ao ler cache do avatar:', error);
      return null;
    }
  }

  /**
   * Armazena avatar no cache
   * @param userId ID do usuário
   * @param url URL do avatar
   */
  setCachedAvatar(userId: string, url: string): void {
    try {
      const cacheKey = `${this.CACHE_KEY_PREFIX}${userId}`;
      const now = Date.now();
      
      const avatarData: CachedAvatar = {
        url,
        timestamp: now,
        expiresAt: now + this.CACHE_DURATION
      };

      localStorage.setItem(cacheKey, JSON.stringify(avatarData));
    } catch (error) {
      console.error('Erro ao salvar cache do avatar:', error);
    }
  }

  /**
   * Remove avatar do cache
   * @param userId ID do usuário
   */
  removeCachedAvatar(userId: string): void {
    try {
      const cacheKey = `${this.CACHE_KEY_PREFIX}${userId}`;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Erro ao remover cache do avatar:', error);
    }
  }

  /**
   * Limpa todos os caches de avatar expirados
   */
  cleanExpiredAvatars(): void {
    try {
      const now = Date.now();
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_KEY_PREFIX)) {
          const cached = localStorage.getItem(key);
          if (cached) {
            try {
              const avatarData: CachedAvatar = JSON.parse(cached);
              if (now > avatarData.expiresAt) {
                localStorage.removeItem(key);
              }
            } catch (error) {
              // Se não conseguir fazer parse, remove o item
              localStorage.removeItem(key);
            }
          }
        }
      });
    } catch (error) {
      console.error('Erro ao limpar cache expirado:', error);
    }
  }

  /**
   * Limpa todo o cache de avatares
   */
  clearAllAvatars(): void {
    try {
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Erro ao limpar todo o cache:', error);
    }
  }
}

export const avatarCacheService = new AvatarCacheService();
